import { Inject, Injectable } from '@angular/core';
import {
  EGI_APP_QUERIES,
  EgiAppQueries,
  USER_ACCOUNT_QUERIES,
  UserAccountQueries,
  UserGroupQueries
} from '@/app/platform/platform-data/queries/types';
import { map } from 'rxjs/operators';
import { ascendingBy } from '@shared/store/entity-state-store';
import {
  UserGroupEntityStore,
  UserGroupExtraState
} from '@/app/platform/platform-data/store/user-group.entity.store';
import { UserGroupEntity } from '@/app/platform/platform-data/models/user-group.entity';
import { combineLatest, Observable } from 'rxjs';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { UserAccountEntity } from '@/app/platform/platform-data/models/user-account.entity';

const byName = ascendingBy<UserGroupEntity>('name');

@Injectable({
  providedIn: 'root'
})
export class UserGroupStoreQueries implements UserGroupQueries {
  constructor(
    private readonly userGroupStore: UserGroupEntityStore,
    @Inject(EGI_APP_QUERIES)
    private readonly egiAppQueries: EgiAppQueries,
    @Inject(USER_ACCOUNT_QUERIES)
    private readonly userAccountQueries: UserAccountQueries
  ) {}

  readonly all$: Observable<UserGroupEntity[]> = this.userGroupStore.selectAll(byName);

  getAll(): UserGroupEntity[] {
    return this.userGroupStore.getAll(byName);
  }

  getHasCache(): boolean {
    return this.userGroupStore.getHasCache();
  }

  one$(id: string): Observable<UserGroupEntity> {
    return this.userGroupStore.selectEntity(id);
  }

  egiAppsForGroup$(groupId: string): Observable<EgiAppEntity[]> {
    return combineLatest([
      this.userGroupStore.select(state => state.groupToAppLinks),
      this.egiAppQueries.all$
    ]).pipe(
      map(([groupToAppLinks, egiApps]) => {
        const linkedAppIds = this.extractLinkedAppIds(groupId, groupToAppLinks);
        return egiApps.filter(app => linkedAppIds.includes(app.id));
      })
    );
  }

  private extractLinkedAppIds(
    groupId: string,
    groupToAppLinks: UserGroupExtraState['groupToAppLinks']
  ): string[] {
    return (groupToAppLinks ?? {})[groupId] ?? [];
  }

  one(id: string): UserGroupEntity {
    return this.userGroupStore.getEntity(id);
  }

  members$(group: UserGroupEntity): Observable<UserAccountEntity[]> {
    return this.userAccountQueries.all$.pipe(
      map(accounts => accounts.filter(a => group?.hasMember(a)))
    );
  }

  nonMembers$(group: UserGroupEntity): Observable<UserAccountEntity[]> {
    return this.userAccountQueries.all$.pipe(
      map(accounts => accounts.filter(a => !group.hasMember(a)))
    );
  }
}
